// src/routes/merchants/catalogue/notNow.ts
import { useCallback, useState } from 'react'

/**
 * "Not now" on a brand suggestion (#494): which brand the user turned down on which transaction. A
 * per-viewer convenience kept in this browser, which the platform allows; a lost key only means a
 * suggestion is offered again.
 */
export const NOT_NOW_KEY = 'tracke5.brandNotNow'
/** Enough for months of dismissals; the oldest go first. */
export const NOT_NOW_MAX = 500

const entry = (txnId: string, brandId: string) => `${txnId}:${brandId}`

function read(): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(NOT_NOW_KEY) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []
  } catch {
    // Private mode throws on access; junk in the key is no list.
    return []
  }
}

function write(entries: readonly string[]): void {
  try {
    localStorage.setItem(NOT_NOW_KEY, JSON.stringify(entries))
  } catch {
    // Private mode, or full: the dismissal holds for this visit only.
  }
}

/**
 * Read once into state (the `useHouseholdId` shape: no store access during render after the first),
 * written from the handler. `hides` is true when every transaction in `txnIds` turned `brandId` down, so
 * a group suggestion comes back if a row joins the group that never saw it.
 */
export function useNotNow(): { hides: (txnIds: readonly string[], brandId: string) => boolean; dismiss: (txnIds: readonly string[], brandId: string) => void } {
  const [entries, setEntries] = useState<readonly string[]>(read)
  const hides = useCallback(
    (txnIds: readonly string[], brandId: string) => {
      if (txnIds.length === 0) return false
      const set = new Set(entries)
      return txnIds.every((id) => set.has(entry(id, brandId)))
    },
    [entries],
  )
  const dismiss = useCallback((txnIds: readonly string[], brandId: string) => {
    const added = txnIds.map((id) => entry(id, brandId))
    // Re-read, so another tab's dismissals since this one mounted are kept.
    const next = [...new Set([...read(), ...added])].slice(-NOT_NOW_MAX)
    write(next)
    setEntries((prev) => [...new Set([...prev, ...added])].slice(-NOT_NOW_MAX))
  }, [])
  return { hides, dismiss }
}
